import { responseLanguageForLocale, type AppLocale } from '@/i18n/locales';

import { sameThreatReportLanguage } from './threat-report-language';
import type { CaseThreatReport } from './threat-report-sharing';

/**
 * Reports that belong to one version family.
 *
 * A family is the exact set of selectors a report was scoped to, in any order,
 * across every Fivecast export. Rerunning the same selectors on a newer export
 * is a new version of the same report.
 *
 * An empty ``creatorIds`` list means every account in that report's export, and
 * those accounts differ per export. Two empty lists therefore match only when
 * the Fivecast and Magnet paths match too.
 */
export function threatReportSelectorGroupKey(
  report: Pick<CaseThreatReport, 'creatorIds' | 'fivecastDataDirectory' | 'magnetDataDirectory'>,
): string {
  const selectors = [
    ...new Set(
      report.creatorIds
        .map((id) => id.trim())
        .filter(Boolean),
    ),
  ].sort();
  if (selectors.length > 0) return JSON.stringify([selectors]);
  return JSON.stringify([
    selectors,
    report.fivecastDataDirectory ?? '',
    report.magnetDataDirectory ?? '',
  ]);
}

/**
 * The report the header should name.
 *
 * A requested epoch stays unmatched until that row is in the list. Falling
 * through to another report would title the screen with the wrong version.
 */
export function resolveThreatReportHeaderVersion(
  reports: readonly CaseThreatReport[],
  currentEpochTimeSecs?: number,
  currentChatKey?: string,
): CaseThreatReport | undefined {
  const requested = currentEpochTimeSecs != null && currentEpochTimeSecs > 0;
  if (requested) {
    return reports.find(
      (report) =>
        report.epochTimeSecs === currentEpochTimeSecs &&
        (currentChatKey == null || report.chatKey === currentChatKey),
    );
  }
  return reports.find((report) => report.status === 'completed' && report.isOwner) ?? reports[0];
}

type VersionIdentity = Pick<CaseThreatReport, 'chatKey' | 'epochTimeSecs'>;

/** Response language an unlabeled in-progress row was requested in. Null is English. */
export type RequestedThreatReportLanguages = ReadonlyMap<string, string | null>;

export function threatReportVersionIdentity(report: VersionIdentity): string {
  return `${report.chatKey}:${report.epochTimeSecs}`;
}

/**
 * Remember the language of each unlabeled in-progress row the first time it is
 * seen. The row does not store a language until generation finishes, and the
 * live UI locale can change while the run stays on screen, so a later locale
 * must not refile it.
 */
export function rememberRequestedThreatReportLanguages(
  remembered: RequestedThreatReportLanguages,
  reports: readonly Pick<CaseThreatReport, 'status' | 'responseLanguage' | 'chatKey' | 'epochTimeSecs'>[],
  locale: AppLocale,
): RequestedThreatReportLanguages {
  let next: Map<string, string | null> | null = null;
  for (const report of reports) {
    if (report.status !== 'inprogress' || report.responseLanguage?.trim()) continue;
    const id = threatReportVersionIdentity(report);
    if (remembered.has(id) || next?.has(id)) continue;
    next ??= new Map(remembered);
    next.set(id, locale === 'en' ? null : responseLanguageForLocale(locale) ?? null);
  }
  return next ?? remembered;
}

/**
 * Language a row contributes to the version menu.
 *
 * A finished report with no stored language is English. An in-progress row has
 * no language until it finishes, and English is not a stand-in for that gap.
 * ``requestedLanguages`` is the language each such row was requested in. A row
 * that is not in the map stays unlabeled and matches only itself.
 */
function versionMenuLanguage(
  report: Pick<CaseThreatReport, 'status' | 'responseLanguage' | 'chatKey' | 'epochTimeSecs'>,
  requestedLanguages?: RequestedThreatReportLanguages,
): string | null | undefined {
  if (report.status !== 'inprogress' || report.responseLanguage?.trim()) {
    // A finished row with no language is English. Normalize the gap so it is
    // not confused with an in-progress row whose language is still unknown.
    return report.responseLanguage ?? null;
  }
  const id = threatReportVersionIdentity(report);
  if (!requestedLanguages?.has(id)) return undefined;
  return requestedLanguages.get(id) ?? null;
}

function sameVersionMenuLanguage(
  candidate: Pick<CaseThreatReport, 'status' | 'responseLanguage' | 'chatKey' | 'epochTimeSecs'>,
  current: Pick<CaseThreatReport, 'status' | 'responseLanguage' | 'chatKey' | 'epochTimeSecs'>,
  requestedLanguages?: RequestedThreatReportLanguages,
): boolean {
  const candidateLanguage = versionMenuLanguage(candidate, requestedLanguages);
  const currentLanguage = versionMenuLanguage(current, requestedLanguages);
  if (candidateLanguage === undefined || currentLanguage === undefined) {
    return candidate.chatKey === current.chatKey && candidate.epochTimeSecs === current.epochTimeSecs;
  }
  return sameThreatReportLanguage(candidateLanguage, currentLanguage);
}

/**
 * Reports that belong in the version menu for the report on screen.
 *
 * The owner sees their own reruns of this selector set, in the same language.
 * A colleague sees that creator's shared reruns of the same set and language.
 * A finished report with no stored language is English. ``requestedLanguages``
 * names the language an in-progress rerun was requested in. The row does not
 * store that until generation finishes, and it must not follow a later UI locale.
 */
export function threatReportVersions(
  reports: readonly CaseThreatReport[],
  current: CaseThreatReport,
  requestedLanguages?: RequestedThreatReportLanguages,
): CaseThreatReport[] {
  const pool = current.isOwner
    ? reports.filter((report) => report.isOwner)
    : reports.filter((report) => report.shared && report.createdBy === current.createdBy);
  const key = threatReportSelectorGroupKey(current);
  return pool.filter(
    (report) =>
      threatReportSelectorGroupKey(report) === key &&
      sameVersionMenuLanguage(report, current, requestedLanguages),
  );
}
